import type { Api } from "../api";
import { navigate } from "../nav";
import { empty, h, label, pill } from "../dom";
import { money, qty, shortDate } from "../format";
import { viewMode, words } from "../orders";
import { modeNotice } from "./shared";
import type { Order, Page } from "../types";

// Orders: customer POs, newest first, filtered by customer and status (find_orders). The filters live in the address
// (#/orders?customer=LTMW&status=ALL) so a list can be linked to and comes back with the browser's Back button.

const STATUSES = ["OPEN", "ALL", "RECEIVED", "VALIDATED", "EXCEPTION", "VENDOR_PO_ISSUED", "PARTIALLY_SHIPPED", "SHIPPED", "INVOICED", "CLOSED"];

const VPO_TONE: Record<string, "late" | "soon" | "good" | "neutral"> = {
  DRAFT: "late", AWAITING_APPROVAL: "soon", APPROVED: "soon", SENT: "good", ACKNOWLEDGED: "good",
};

export async function orders(api: Api, query: URLSearchParams): Promise<HTMLElement> {
  const customer = (query.get("customer") ?? "").slice(0, 100);
  const status = STATUSES.includes(query.get("status") ?? "") ? query.get("status")! : "OPEN";
  const cursor = /^[0-9]{1,6}$/.test(query.get("cursor") ?? "") ? query.get("cursor")! : undefined;
  const asked = ["LIVE", "SIMULATION"].includes(query.get("mode") ?? "") ? query.get("mode")! : undefined;
  const page = await api.tool<Page<Order>>("find_orders", {
    status, limit: 50, ...(customer ? { customer } : {}), ...(cursor ? { cursor } : {}), ...(asked ? { mode: asked } : {}),
  });
  const view = viewMode(query, page.mode); // the platform's mode comes back with the page

  const customerInput = h("input", { id: "customer", type: "search", value: customer, placeholder: "LTMW, Creative…", maxlength: 100 });
  const statusSelect = h("select", { id: "status" }, STATUSES.map((s) => h("option", { value: s, selected: s === status }, s === "OPEN" ? "Open" : words(s))));
  const modeSelect = h("select", { id: "mode" }, (["LIVE", "SIMULATION"] as const).map((m) =>
    h("option", { value: m, selected: m === view.mode }, `${m === "LIVE" ? "Live orders" : "Simulation copies"}${m === page.mode ? " (the platform's mode)" : ""}`)));
  const go = (extra: Record<string, string> = {}) => {
    const q = new URLSearchParams({ ...(customerInput.value.trim() ? { customer: customerInput.value.trim() } : {}), status: statusSelect.value,
      ...(modeSelect.value !== page.mode ? { mode: modeSelect.value } : {}), ...extra });
    navigate(`/orders?${q}`);
  };
  const form = h("form", { class: "filters", role: "search" },
    h("div", { class: "field" }, h("label", { for: "customer" }, "Customer"), customerInput),
    h("div", { class: "field" }, h("label", { for: "status" }, "Status"), statusSelect),
    h("div", { class: "field" }, h("label", { for: "mode" }, "Showing"), modeSelect),
    h("button", { type: "submit", class: "button" }, "Show"));
  form.addEventListener("submit", (e) => { e.preventDefault(); go(); });
  statusSelect.addEventListener("change", () => go());
  modeSelect.addEventListener("change", () => go());

  const total = page.rows.reduce((s, o) => s + (o.totals?.sellValue ?? 0), 0);

  return h("div", { class: "page" },
    h("div", { class: "stack-tight" },
      h("h1", { class: "title" }, "Orders"),
      h("p", { class: "lede" }, page.rows.length
        ? `${page.rows.length}${page.next_cursor ? "+" : ""} customer ${page.rows.length === 1 ? "PO" : "POs"}${status === "OPEN" ? " open" : ""}` +
          `${customer ? ` for “${customer}”` : ""}, ${money(total)} at sell.`
        : "No customer POs match.")),
    form,
    view.other ? modeNotice(view.mode, page.mode) : null,
    page.rows.length
      ? h("div", { class: "panel scroll" }, h("table", { class: "tbl" },
          h("thead", null, h("tr", null,
            h("th", { scope: "col" }, "PO"), h("th", { scope: "col" }, "Customer"), h("th", { scope: "col" }, "Dated"),
            h("th", { scope: "col" }, "Status"), h("th", { scope: "col", class: "r" }, "Ordered"), h("th", { scope: "col", class: "r" }, "Shipped"),
            h("th", { scope: "col", class: "r" }, "Sell"), h("th", { scope: "col" }, "Vendor PO"), h("th", { scope: "col" }, "Exceptions"))),
          h("tbody", null, page.rows.map((o) => h("tr", null,
            h("td", { class: "num strong" }, h("a", { href: `/order/${o.customerPoId}${view.param}` }, o.poNumber)),
            h("td", null, o.customer?.name ?? "", o.endCustomer ? h("div", { class: "caption" }, `for ${o.endCustomer.name}`) : null),
            h("td", { class: "num" }, shortDate(o.poDate)),
            h("td", null, words(o.status)),
            h("td", { class: "num r" }, qty(o.totals?.orderedEa)),
            h("td", { class: "num r" }, qty(o.totals?.shippedEa ?? 0)),
            h("td", { class: "num r" }, money(o.totals?.sellValue)),
            h("td", null, o.vendorPos.length
              ? o.vendorPos.map((v) => h("div", null, h("span", { class: "num" }, v.number), " ",
                  pill(words(v.status).toLowerCase(), VPO_TONE[v.status] ?? "neutral")))
              : h("span", { class: "muted" }, "None")),
            h("td", null, o.openExceptions ? pill(`${o.openExceptions} open`, "late") : h("span", { class: "muted" }, "None")))))))
      : view.mode === "LIVE" && !view.other
        ? h("div", { class: "empty" }, h("span", { class: "subheading" }, "No live orders"),
            h("span", null, "The platform is LIVE and has no live orders here yet. "),
            h("a", { href: `/orders?${new URLSearchParams({ ...(customer ? { customer } : {}), status, mode: "SIMULATION" })}` }, "Show the simulation copies"))
        : empty("Nothing here", "Try another customer, or All for closed orders too."),
    page.next_cursor
      ? h("div", null, h("a", { class: "button", href: `/orders?${new URLSearchParams({ ...(customer ? { customer } : {}), status, cursor: page.next_cursor, ...(view.other ? { mode: view.mode } : {}) })}` }, "Next 50"))
      : null,
    label(view.mode === "SIMULATION" ? "Simulation copies" : "Live orders"));
}
